import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import NewPersonCard from 'app/components/People/PeopleContent/PeopleList/NewPersonCard';
import { Sparkle } from 'app/components/shared/icons';
import { AnimatePresence, useAnimate } from 'framer-motion';
import useIsMobile from 'hooks/useIsMobile';
import { isNLPLoadingSelector } from 'store/reducers/nlp/nlp-reducer';
import { setShowingLoadingScreen } from 'store/reducers/people-steps-reducer/people-steps-reducer';
import {
  isPeopleLoadingSelector,
  previousFiltersLengthSelector,
} from 'store/reducers/people/people-reducer';

import {
  PeopleStepsOneLoadingCardsContainer,
  PeopleStepsOneLoadingContainer,
  PeopleStepsOneLoadingGif,
  PeopleStepsOneLoadingTitle,
  PeopleStepsOneLoadingTitleContainer,
  PeopleStepsOneLoadingWrapper,
} from './styled';

export default function PeopleStepsOneLoading() {
  const isMobile = useIsMobile();
  const [scope, animate] = useAnimate();
  const dispatch = useDispatch();
  const isNlpLoading = useSelector(isNLPLoadingSelector);
  const filters = useSelector(previousFiltersLengthSelector);
  const isPeopleLoading = useSelector(isPeopleLoadingSelector);

  async function animateLoading() {
    await animate(scope.current, { opacity: [0, 1] }, { duration: 0.2 });
    await animate(scope.current, { top: 0, bottom: -500 }, { duration: 0.5 });
    await animate(scope.current, { opacity: [1, 0] }, { delay: 1.8 });
  }

  const isLoading = useMemo<boolean>(() => {
    return isNlpLoading || (isPeopleLoading && filters > 0);
  }, [isNlpLoading, isPeopleLoading, filters]);

  useEffect(() => {
    if (!isLoading) {
      animateLoading();
    }
  }, [isLoading]);

  useEffect(() => {
    dispatch(setShowingLoadingScreen(true));
  }, []);

  useEffect(() => {
    if (!isLoading) {
      const timeout = setTimeout(() => {
        dispatch(setShowingLoadingScreen(false));
      }, 2000);

      return () => clearTimeout(timeout);
    }
  }, [isLoading]);

  return (
    <PeopleStepsOneLoadingWrapper>
      <AnimatePresence>{isLoading ? <PeopleStepsOneLoadingGif /> : null}</AnimatePresence>
      <AnimatePresence>
        {!isLoading ? (
          <PeopleStepsOneLoadingContainer ref={scope}>
            <PeopleStepsOneLoadingTitleContainer>
              <Sparkle width={24} height={24} fill="var(--color-primary)" />
              <PeopleStepsOneLoadingTitle>Preparing the results...</PeopleStepsOneLoadingTitle>
            </PeopleStepsOneLoadingTitleContainer>
            <PeopleStepsOneLoadingCardsContainer>
              {Array.from({ length: 3 }).map((_, index) => (
                <NewPersonCard.Skeleton key={index} />
              ))}
            </PeopleStepsOneLoadingCardsContainer>
          </PeopleStepsOneLoadingContainer>
        ) : null}
      </AnimatePresence>
    </PeopleStepsOneLoadingWrapper>
  );
}
